/**
 * GraphOps 2 compiler-response types. The backend's model-facing vocabulary
 * may contain template text, but executable browser operations never do.
 *
 * These are the ops a plan from POST /api/logiblocks-agent/chat/ is made
 * of. applyGraphOps.ts folds a list of these onto the current ReactFlow
 * graph via the existing graphUtils.ts functions — never a separate bulk-
 * mutation path.
 */

import { isNodeType, type NodeType } from '../nodes/registry';
import { VariableSource, type VariableDesc } from '@/proto/common';
import { decodeCollectVariableDescriptors } from '../nodes/aiCallAction/collectVariableDescriptors';
import { cloneBoundedProtoJson } from './authoringSchema';

export type FieldValueKind =
  | 'string'
  | 'number'
  | 'bool'
  | 'string_list'
  | 'variable_ref'
  | 'team_recipient_list'
  | 'enum'
  | 'enum_list'
  | 'number_list'
  | 'time_of_day_list'
  | 'date_range_list'
  | 'template_chunks'
  | 'protobuf_json';

export type GraphOpsVersion = 2;

export function readGraphOpsVersion(value: unknown): GraphOpsVersion {
  if (value === 2) return 2;
  throw new RawOpParseError(
    'Unsupported GraphOps version. Reload the workflow.'
  );
}

export interface VariableRefValue {
  /** An existing node id (UUID string) or a plan-local $ref. */
  nodeRef: string;
  /** Published names stay whole; derived fields are a separate identity part. */
  variableName: string;
  derivedVariableName: string;
  source: VariableSource;
}

/**
 * One team_recipient_list entry: a team, plus optionally specific people in
 * it. Empty/absent userIds means everyone on the team (TeamNotifRecipients
 * selectAll=true); a non-empty list means only those people.
 */
export interface TeamRecipientEntry {
  teamId: string;
  userIds?: string[];
}

/** One schedule_trigger_v2.schedule_times entry (24-hour time). */
export interface TimeOfDayEntry {
  hour: number;
  minute: number;
}

/** One filterControlFlow date-range entry (e.g. scheduledPickupDate). Maps
 * to the proto's map<int32, FilterDateRange> -- entries are keyed by their
 * position in this list on conversion, see applyGraphOps.ts. */
export interface DateRangeEntry {
  period: string;
  start: string;
  end: string;
}

export interface TemplateChunkVariable {
  nodeRef: string;
  variableName: string;
  derivedVariableName: string;
  source: VariableSource;
}

export type TemplateChunkValue =
  { text: string } | { variable: TemplateChunkVariable };

export interface FieldValue {
  kind: FieldValueKind;
  stringValue?: string;
  numberValue?: number;
  boolValue?: boolean;
  stringListValue?: string[];
  variableRefValue?: VariableRefValue;
  /** Only for email_action's to/cc/bcc — see TeamRecipientEntry. */
  teamRecipientListValue?: TeamRecipientEntry[];
  /** A proto enum value NAME (e.g. "DAY"), never its numeric value. */
  enumValue?: string;
  enumListValue?: string[];
  numberListValue?: number[];
  timeOfDayListValue?: TimeOfDayEntry[];
  dateRangeListValue?: DateRangeEntry[];
  /** Server-compiled structured template value. */
  templateChunksValue?: TemplateChunkValue[];
  protobufJsonValue?: unknown;
  clearPaths?: string[];
}

export interface ConfigureField {
  name: string;
  value: FieldValue;
}

export interface AddNodeOp {
  op: 'add_node';
  ref: string;
  /** Existing node id, a $ref, or '' only when the graph is empty. */
  parent: string;
  nodeType: NodeType;
  displayName?: string | null;
}

export interface ConfigureNodeOp {
  op: 'configure_node';
  /** Existing node id or $ref. */
  node: string;
  fields: ConfigureField[];
}

export interface AddBranchOp {
  op: 'add_branch';
  /** Existing node id or $ref; must resolve to a 'paths' node. */
  pathsNode: string;
  ref: string;
  label?: string | null;
}

export interface AddConditionOp {
  op: 'add_condition';
  /** Existing node id or $ref; must resolve to a 'conditional' node. */
  conditional: string;
  lhs: VariableRefValue;
  /** ComparisonOp enum name, e.g. "COMPARISON_EQUALS". */
  operator: string;
  rhs?: FieldValue | null;
  rhs2?: FieldValue | null;
}

export interface DeleteNodeOp {
  op: 'delete_node';
  /** Existing node id only, never a $ref. */
  node: string;
}

export interface RenameNodeOp {
  op: 'rename_node';
  /** Existing node id or $ref. */
  node: string;
  displayName: string;
}

export interface ReplaceNodeUserVariablesOp {
  /** Trusted compiler-only operation. The model-facing parser must never accept it. */
  op: 'replace_node_user_variables';
  node: string;
  agentId: string;
  descriptorHash: string;
  userVariables: VariableDesc[];
  collectShipmentId: boolean;
}

export interface ReplaceNodeConditionsOp {
  /** Trusted compiler-only operation. The model-facing parser must never accept it. */
  op: 'replace_node_conditions';
  node: string;
  conditions: Array<Omit<AddConditionOp, 'op' | 'conditional'>>;
}

export interface WrapWithPathsOp {
  /** Trusted compiler-only structural operation for an atomic linear-to-split edit. */
  op: 'wrap_with_paths';
  parent: string;
  pathsRef: string;
  branchRefs: string[];
}

export type ModelGraphOp =
  | AddNodeOp
  | ConfigureNodeOp
  | AddBranchOp
  | AddConditionOp
  | DeleteNodeOp
  | RenameNodeOp;

export type GraphOp =
  | ModelGraphOp
  | ReplaceNodeUserVariablesOp
  | ReplaceNodeConditionsOp
  | WrapWithPathsOp;

/** A plan-local $ref alias, e.g. "$slack_1". */
export const REF_PATTERN = /^\$[a-z0-9_]+$/;

export function isRef(value: string): boolean {
  return REF_PATTERN.test(value);
}

/** Thrown by parseRawOps when the backend's ops array doesn't match the expected shape. */
export class RawOpParseError extends Error {}

// The `context` parameter these parsers share is the value's location in the
// raw ops array, such as "ops[2].fields[0].name". It is prefixed onto the
// error message. The error from a malformed plan then points at the exact
// field that was wrong.
function asRecord(value: unknown, context: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null) {
    throw new RawOpParseError(`${context}: expected an object`);
  }
  return value as Record<string, unknown>;
}

function asString(value: unknown, context: string): string {
  if (typeof value !== 'string') {
    throw new RawOpParseError(`${context}: expected a string`);
  }
  return value;
}

function asNonEmptyString(value: unknown, context: string): string {
  const result = asString(value, context);
  if (result.length === 0) {
    throw new RawOpParseError(`${context}: expected a non-empty string`);
  }
  return result;
}

function asNumber(value: unknown, context: string): number {
  if (typeof value !== 'number') {
    throw new RawOpParseError(`${context}: expected a number`);
  }
  return value;
}

function asBoolean(value: unknown, context: string): boolean {
  if (typeof value !== 'boolean') {
    throw new RawOpParseError(`${context}: expected a boolean`);
  }
  return value;
}

function rejectUnexpectedKeys(
  value: Record<string, unknown>,
  allowed: readonly string[],
  context: string
): void {
  const allowedKeys = new Set(allowed);
  const unexpected = Object.keys(value).filter((key) => !allowedKeys.has(key));
  if (unexpected.length > 0) {
    throw new RawOpParseError(
      `${context}: unexpected keys ${unexpected.sort().join(', ')}`
    );
  }
}

function parseFieldValue(raw: unknown, context: string): FieldValue {
  const r = asRecord(raw, context);
  const kind = asString(r.kind, `${context}.kind`) as FieldValueKind;
  switch (kind) {
    case 'protobuf_json': {
      rejectUnexpectedKeys(
        r,
        ['kind', 'protobuf_json_value', 'clear_paths'],
        context
      );
      if (!r.protobuf_json_value || typeof r.protobuf_json_value !== 'object')
        throw new RawOpParseError(
          `${context}.protobuf_json_value: expected a message, map or list`
        );
      const clearPaths = r.clear_paths ?? [];
      if (
        !Array.isArray(clearPaths) ||
        clearPaths.length > 128 ||
        clearPaths.some((path) => typeof path !== 'string' || path.length > 256)
      )
        throw new RawOpParseError(`${context}.clear_paths: invalid paths`);
      try {
        return {
          kind,
          protobufJsonValue: cloneBoundedProtoJson(r.protobuf_json_value),
          clearPaths,
        };
      } catch (error) {
        throw new RawOpParseError(
          `${context}: ${error instanceof Error ? error.message : 'invalid protobuf JSON'}`
        );
      }
    }
    case 'string':
      return { kind, stringValue: r.string_value as string | undefined };
    case 'number':
      return { kind, numberValue: r.number_value as number | undefined };
    case 'bool':
      return { kind, boolValue: r.bool_value as boolean | undefined };
    case 'string_list':
      return {
        kind,
        stringListValue: r.string_list_value as string[] | undefined,
      };
    case 'team_recipient_list': {
      const rawEntries = r.team_recipient_list_value;
      if (rawEntries === undefined) {
        return { kind, teamRecipientListValue: undefined };
      }
      if (!Array.isArray(rawEntries)) {
        throw new RawOpParseError(
          `${context}.team_recipient_list_value: expected an array`
        );
      }
      return {
        kind,
        teamRecipientListValue: rawEntries.map((rawEntry, entryIndex) => {
          const entryContext = `${context}.team_recipient_list_value[${entryIndex}]`;
          const entryRecord = asRecord(rawEntry, entryContext);
          return {
            teamId: asString(entryRecord.team_id, `${entryContext}.team_id`),
            userIds: entryRecord.user_ids as string[] | undefined,
          };
        }),
      };
    }
    case 'variable_ref': {
      return {
        kind,
        variableRefValue: parseVariableRef(
          r.variable_ref_value,
          `${context}.variable_ref_value`
        ),
      };
    }
    case 'enum':
      return { kind, enumValue: r.enum_value as string | undefined };
    case 'enum_list':
      return { kind, enumListValue: r.enum_list_value as string[] | undefined };
    case 'number_list':
      return {
        kind,
        numberListValue: r.number_list_value as number[] | undefined,
      };
    case 'time_of_day_list': {
      const rawEntries = r.time_of_day_list_value;
      if (rawEntries === undefined) {
        return { kind, timeOfDayListValue: undefined };
      }
      if (!Array.isArray(rawEntries)) {
        throw new RawOpParseError(
          `${context}.time_of_day_list_value: expected an array`
        );
      }
      return {
        kind,
        timeOfDayListValue: rawEntries.map((rawEntry, entryIndex) => {
          const entryContext = `${context}.time_of_day_list_value[${entryIndex}]`;
          const entryRecord = asRecord(rawEntry, entryContext);
          return {
            hour: asNumber(entryRecord.hour, `${entryContext}.hour`),
            minute: asNumber(entryRecord.minute, `${entryContext}.minute`),
          };
        }),
      };
    }
    case 'date_range_list': {
      const rawEntries = r.date_range_list_value;
      if (rawEntries === undefined) {
        return { kind, dateRangeListValue: undefined };
      }
      if (!Array.isArray(rawEntries)) {
        throw new RawOpParseError(
          `${context}.date_range_list_value: expected an array`
        );
      }
      return {
        kind,
        dateRangeListValue: rawEntries.map((rawEntry, entryIndex) => {
          const entryContext = `${context}.date_range_list_value[${entryIndex}]`;
          const entryRecord = asRecord(rawEntry, entryContext);
          return {
            period: asString(entryRecord.period, `${entryContext}.period`),
            start: asString(entryRecord.start, `${entryContext}.start`),
            end: asString(entryRecord.end, `${entryContext}.end`),
          };
        }),
      };
    }
    case 'template_chunks': {
      rejectUnexpectedKeys(r, ['kind', 'template_chunks_value'], context);
      const rawChunks = r.template_chunks_value;
      if (!Array.isArray(rawChunks)) {
        throw new RawOpParseError(
          `${context}.template_chunks_value: expected an array`
        );
      }
      return {
        kind,
        templateChunksValue: rawChunks.map((rawChunk, chunkIndex) => {
          const chunkContext = `${context}.template_chunks_value[${chunkIndex}]`;
          const chunk = asRecord(rawChunk, chunkContext);
          if (Object.keys(chunk).length !== 1) {
            throw new RawOpParseError(
              `${chunkContext}: expected exactly one of text or variable`
            );
          }
          if ('text' in chunk) {
            const text = asString(chunk.text, `${chunkContext}.text`);
            if (text.includes('{{'))
              throw new RawOpParseError(
                `${chunkContext}: uncompiled template text`
              );
            return { text };
          }
          return {
            variable: parseVariableRef(
              chunk.variable,
              `${chunkContext}.variable`
            ),
          };
        }),
      };
    }
    default:
      throw new RawOpParseError(
        `${context}.kind: unrecognized kind ${String(kind)}`
      );
  }
}

function parseVariableRef(raw: unknown, context: string): VariableRefValue {
  const r = asRecord(raw, context);
  rejectUnexpectedKeys(
    r,
    ['node_ref', 'variable_name', 'derived_variable_name', 'source'],
    context
  );
  const source = asNumber(r.source, `${context}.source`);
  if (
    ![
      VariableSource.SOURCE_NODE_TYPE,
      VariableSource.SOURCE_USER,
      VariableSource.SOURCE_SYSTEM,
    ].includes(source)
  )
    throw new RawOpParseError(`${context}.source: invalid variable source`);
  return {
    nodeRef: asNonEmptyString(r.node_ref, `${context}.node_ref`),
    variableName: asNonEmptyString(r.variable_name, `${context}.variable_name`),
    derivedVariableName: asString(
      r.derived_variable_name,
      `${context}.derived_variable_name`
    ),
    source,
  };
}

/** Reads a node type name from a raw op. Throws if no such node type exists. */
function asNodeType(value: unknown, context: string): NodeType {
  const name = asString(value, context);
  if (!isNodeType(name)) {
    throw new RawOpParseError(`${context}: unknown node type ${name}`);
  }
  return name;
}

/**
 * Converts compiled response ops JSON (snake_case) into typed GraphOp[].
 * Throws RawOpParseError on the first malformed op rather than silently
 * dropping or guessing — callers should treat that as "this plan can't be
 * applied" and surface it to the user, same as an applyGraphOps failure.
 */
function parseRawOpsInternal(raw: unknown[]): ModelGraphOp[] {
  return raw.map((rawOp, index) => {
    const context = `ops[${index}]`;
    const r = asRecord(rawOp, context);
    const op = asString(r.op, `${context}.op`);

    switch (op) {
      case 'add_node':
        return {
          op,
          ref: asString(r.ref, `${context}.ref`),
          parent: asString(r.parent, `${context}.parent`),
          nodeType: asNodeType(r.node_type, `${context}.node_type`),
          displayName: (r.display_name as string | null | undefined) ?? null,
        } satisfies AddNodeOp;

      case 'configure_node': {
        const rawFields = r.fields;
        if (!Array.isArray(rawFields)) {
          throw new RawOpParseError(`${context}.fields: expected an array`);
        }
        return {
          op,
          node: asString(r.node, `${context}.node`),
          fields: rawFields.map((rawField, fieldIndex) => {
            const fieldContext = `${context}.fields[${fieldIndex}]`;
            const fieldRecord = asRecord(rawField, fieldContext);
            return {
              name: asString(fieldRecord.name, `${fieldContext}.name`),
              value: parseFieldValue(
                fieldRecord.value,
                `${fieldContext}.value`
              ),
            };
          }),
        } satisfies ConfigureNodeOp;
      }

      case 'add_branch':
        return {
          op,
          pathsNode: asString(r.paths_node, `${context}.paths_node`),
          ref: asString(r.ref, `${context}.ref`),
          label: (r.label as string | null | undefined) ?? null,
        } satisfies AddBranchOp;

      case 'add_condition':
        return {
          op,
          conditional: asString(r.conditional, `${context}.conditional`),
          lhs: parseVariableRef(r.lhs, `${context}.lhs`),
          operator: asString(r.operator, `${context}.operator`),
          rhs: r.rhs ? parseFieldValue(r.rhs, `${context}.rhs`) : null,
          rhs2: r.rhs2 ? parseFieldValue(r.rhs2, `${context}.rhs2`) : null,
        } satisfies AddConditionOp;

      case 'delete_node':
        return {
          op,
          node: asString(r.node, `${context}.node`),
        } satisfies DeleteNodeOp;

      case 'rename_node':
        return {
          op,
          node: asString(r.node, `${context}.node`),
          displayName: asString(r.display_name, `${context}.display_name`),
        } satisfies RenameNodeOp;

      default:
        throw new RawOpParseError(`${context}.op: unrecognized op ${op}`);
    }
  });
}

export function parseRawOps(raw: unknown[], version?: unknown): ModelGraphOp[] {
  readGraphOpsVersion(version);
  return parseRawOpsInternal(raw);
}

/**
 * Parse operations emitted by a trusted configuration/edit compiler. Format
 * selection changes value codecs, never the compiler-only operation allowlist.
 *
 * `replace_node_user_variables` is deliberately accepted only here. Keeping it out of
 * parseRawOps prevents a model-authored v1 response from writing server-derived descriptors.
 */
export function parseCompilerRawOps(
  raw: unknown[],
  version?: unknown
): GraphOp[] {
  readGraphOpsVersion(version);
  return raw.map((rawOp, index) => {
    const context = `ops[${index}]`;
    const record = asRecord(rawOp, context);
    if (
      record.op !== 'replace_node_user_variables' &&
      record.op !== 'replace_node_conditions' &&
      record.op !== 'wrap_with_paths'
    ) {
      return parseRawOpsInternal([rawOp])[0];
    }
    if (record.op === 'replace_node_conditions') {
      rejectUnexpectedKeys(record, ['op', 'node', 'conditions'], context);
      const node = asNonEmptyString(record.node, `${context}.node`);
      if (isRef(node)) {
        throw new RawOpParseError(
          `${context}.node: compiler operations require a saved node id`
        );
      }
      if (!Array.isArray(record.conditions) || record.conditions.length === 0) {
        throw new RawOpParseError(
          `${context}.conditions: expected a non-empty array`
        );
      }
      const conditions = record.conditions.map((condition, conditionIndex) => {
        const parsed = parseRawOps(
          [
            {
              op: 'add_condition',
              conditional: node,
              ...asRecord(
                condition,
                `${context}.conditions[${conditionIndex}]`
              ),
            },
          ],
          version
        )[0];
        if (parsed.op !== 'add_condition') {
          throw new RawOpParseError(
            `${context}.conditions[${conditionIndex}]: invalid condition`
          );
        }
        const { op: _op, conditional: _conditional, ...value } = parsed;
        return value;
      });
      return {
        op: 'replace_node_conditions',
        node,
        conditions,
      } satisfies ReplaceNodeConditionsOp;
    }
    if (record.op === 'wrap_with_paths') {
      rejectUnexpectedKeys(
        record,
        ['op', 'parent', 'paths_ref', 'branch_refs'],
        context
      );
      const parent = asNonEmptyString(record.parent, `${context}.parent`);
      if (isRef(parent)) {
        throw new RawOpParseError(
          `${context}.parent: compiler operations require a saved node id`
        );
      }
      const pathsRef = asNonEmptyString(
        record.paths_ref,
        `${context}.paths_ref`
      );
      if (!isRef(pathsRef) || pathsRef.startsWith('$existing_')) {
        throw new RawOpParseError(`${context}.paths_ref: invalid new-node ref`);
      }
      if (
        !Array.isArray(record.branch_refs) ||
        record.branch_refs.length < 2 ||
        record.branch_refs.length > 10
      ) {
        throw new RawOpParseError(
          `${context}.branch_refs: expected 2 to 10 refs`
        );
      }
      const branchRefs = record.branch_refs.map((value, branchIndex) => {
        const ref = asNonEmptyString(
          value,
          `${context}.branch_refs[${branchIndex}]`
        );
        if (!isRef(ref) || ref.startsWith('$existing_')) {
          throw new RawOpParseError(
            `${context}.branch_refs[${branchIndex}]: invalid new-node ref`
          );
        }
        return ref;
      });
      if (new Set([pathsRef, ...branchRefs]).size !== branchRefs.length + 1) {
        throw new RawOpParseError(`${context}: Paths refs must be unique`);
      }
      return {
        op: 'wrap_with_paths',
        parent,
        pathsRef,
        branchRefs,
      } satisfies WrapWithPathsOp;
    }
    rejectUnexpectedKeys(
      record,
      [
        'op',
        'node',
        'agent_id',
        'descriptor_hash',
        'user_variables',
        'collect_shipment_id',
      ],
      context
    );
    const rawVariables = record.user_variables;
    if (!Array.isArray(rawVariables)) {
      throw new RawOpParseError(`${context}.user_variables: expected an array`);
    }
    try {
      const node = asNonEmptyString(record.node, `${context}.node`);
      if (isRef(node)) {
        throw new RawOpParseError(
          `${context}.node: compiler operations require a saved node id`
        );
      }
      return {
        op: 'replace_node_user_variables',
        node,
        agentId: asNonEmptyString(record.agent_id, `${context}.agent_id`),
        descriptorHash: asNonEmptyString(
          record.descriptor_hash,
          `${context}.descriptor_hash`
        ),
        userVariables: decodeCollectVariableDescriptors(
          rawVariables,
          `${context}.user_variables`
        ),
        collectShipmentId: asBoolean(
          record.collect_shipment_id,
          `${context}.collect_shipment_id`
        ),
      } satisfies ReplaceNodeUserVariablesOp;
    } catch (error) {
      if (error instanceof RawOpParseError) throw error;
      throw new RawOpParseError(
        error instanceof Error ? error.message : String(error)
      );
    }
  });
}
